
/**
 * Step 2: 目标人群分析
 */

import React from 'react';
import {
    Card, Button, Space, Tag, Input, Empty, Spin, Row, Col,
    Typography, Badge,
} from 'antd';
import {
    BulbOutlined, TeamOutlined, EditOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import type { TargetAudience } from '../types';
import { AudienceCard } from './common';
import { useAudienceStep } from './hooks/useAudienceStep';

const { Text } = Typography;
const { TextArea } = Input;

interface AudienceStepProps {
    audiences: TargetAudience[];
    onSelect: (selectedIds: string[]) => void;
    onCustomInputChange?: (input: string) => void;
    customInput?: string;
    onAnalyze?: (customInput?: string) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
    submitting?: boolean;
    analyzed?: boolean;
}

export const AudienceStep: React.FC<AudienceStepProps> = ({
    audiences,
    onSelect,
    onCustomInputChange,
    customInput = '',
    onAnalyze,
    onNext,
    onBack,
    loading,
    submitting,
}) => {
    const {
        showCustomInput,
        setShowCustomInput,
        selectedIds,
        groupedAudiences,
        handleToggle,
        handleSelectAll,
        handleReAnalyze
    } = useAudienceStep({
        audiences,
        onSelect,
        onAnalyze,
        customInput
    });

    // 渲染自定义输入区域
    const renderCustomInputSection = () => {
        if (!onAnalyze) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Button
                        type="dashed"
                        icon={<EditOutlined />}
                        onClick={() => setShowCustomInput(!showCustomInput)}
                    >
                        {showCustomInput ? '收起' : '添加自定义人群描述'}
                    </Button>
                    {showCustomInput && (
                        <div>
                            <TextArea
                                value={customInput}
                                onChange={e => onCustomInputChange?.(e.target.value)}
                                placeholder="描述您想要的特定人群，AI会基于您的输入进行优化和补充..."
                                autoSize={{ minRows: 2, maxRows: 4 }}
                            />
                            <Button
                                type="primary"
                                size="small"
                                style={{ marginTop: 8 }}
                                onClick={handleReAnalyze}
                                loading={loading}
                                icon={<BulbOutlined />}
                            >
                                重新分析
                            </Button>
                        </div>
                    )}
                </Space>
            </div>
        );
    };

    // 渲染人群列表
    const renderAudienceList = () => {
        // 加载中且无人群
        if (audiences.length === 0 && loading) {
            return (
                <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">AI 正在分析目标人群...</Text>
                    </div>
                </div>
            );
        }

        // 无人群且不在加载
        if (audiences.length === 0 && !loading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                        description={
                            <span>
                                暂无目标人群数据<br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    点击下方按钮开始AI分析，为您推荐最匹配的目标受众
                                </Text>
                            </span>
                        }
                    >
                        <div style={{ maxWidth: 500, margin: '24px auto 0' }}>
                            <TextArea
                                value={customInput}
                                onChange={e => onCustomInputChange?.(e.target.value)}
                                placeholder="（可选）在此输入您对目标人群的特殊要求或限制..."
                                autoSize={{ minRows: 2, maxRows: 4 }}
                                style={{ marginBottom: 16 }}
                            />
                            <Button
                                type="primary"
                                size="large"
                                icon={<BulbOutlined />}
                                onClick={handleReAnalyze}
                                disabled={!onAnalyze}
                            >
                                开始分析目标人群
                            </Button>
                        </div>
                    </Empty>
                </div>
            );
        }

        // 有人群时显示卡片
        return (
            <div>
                {loading && (
                    <div style={{
                        textAlign: 'center',
                        padding: '12px 0 24px',
                        borderBottom: '1px dashed #e8e8e8',
                        marginBottom: 16
                    }}>
                        <Spin size="small" />
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            正在生成更多人群... ({audiences.length} 个已完成)
                        </Text>
                    </div>
                )}
                {Object.entries(groupedAudiences).map(([category, categoryAudiences]) => (
                    <div key={category} style={{ marginBottom: 24 }}>
                        <div style={{ marginBottom: 12 }}>
                            <Tag color={category === '通用' ? 'gold' : 'cyan'} style={{ fontWeight: 600 }}>
                                {category} ({categoryAudiences.length})
                            </Tag>
                        </div>
                        <Row gutter={[12, 12]}>
                            {categoryAudiences.map(audience => (
                                <Col key={audience.id} xs={24} sm={12} lg={8}>
                                    <AudienceCard
                                        audience={audience}
                                        selected={audience.selected || false}
                                        onToggle={() => handleToggle(audience.id)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="audience-step">
            <Card
                title={
                    <Space>
                        <TeamOutlined />
                        目标人群分析
                        {audiences.length > 0 && (
                            <Badge count={audiences.length} style={{ backgroundColor: '#52c41a' }} />
                        )}
                    </Space>
                }
                bordered={false}
                extra={
                    <Space>
                        {audiences.length > 0 && (
                            <>
                                <Text type="secondary">
                                    已选 {selectedIds.length}/{audiences.length}
                                </Text>
                                <Button size="small" onClick={handleSelectAll}>
                                    {selectedIds.length === audiences.length ? '取消全选' : '全选'}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                {renderCustomInputSection()}
                {renderAudienceList()}
            </Card>

            {/* 底部操作栏 */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
                <Button size="large" onClick={onBack} disabled={submitting}>
                    上一步
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={onNext}
                    disabled={selectedIds.length === 0 || submitting}
                    loading={submitting}
                    icon={<CheckCircleOutlined />}
                >
                    {submitting ? '正在推荐场景...' : `下一步：推荐使用场景 (${selectedIds.length} 个人群)`}
                </Button>
            </div>
        </div>
    );
};

export default AudienceStep;
